/**
 * CEO Command Center - Initiative Store
 * 
 * Zustand store for managing initiatives, their tasks, and documents.
 * Data is persisted to localStorage.
 * 
 * @module stores/initiativeStore
 * @version 10.1.0
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Initiative,
  InitiativeTask,
  InitiativeDocument,
  InitiativeWithMetrics,
  InitiativeFormData,
} from '../types/initiative';
import { DEFAULT_INITIATIVE } from '../types/initiative';
import { APP_CONFIG } from '../config/constants';

// Helper to refresh signals when initiative tasks change
// Uses dynamic import to avoid circular dependency
const refreshGlobalSignals = async () => {
  try {
    const { useDataStoreSQLite } = await import('./dataStoreSQLite');
    useDataStoreSQLite.getState().refreshSignals();
  } catch (error) {
    console.warn('[InitiativeStore] Could not refresh global signals:', error);
  }
};

// ============================================
// TYPES
// ============================================

interface InitiativeState {
  // Data
  initiatives: Initiative[];
  tasks: InitiativeTask[];
  documents: InitiativeDocument[];
  
  // UI State
  selectedInitiativeId: string | null;
  
  // Initiative CRUD
  createInitiative: (data: InitiativeFormData) => Initiative;
  updateInitiative: (id: string, updates: Partial<InitiativeFormData>) => void;
  deleteInitiative: (id: string) => void;
  
  // Task CRUD
  createTask: (initiativeId: string, data: Partial<InitiativeTask>) => InitiativeTask;
  updateTask: (id: string, updates: Partial<InitiativeTask>) => void;
  deleteTask: (id: string) => void;
  toggleTaskStatus: (id: string) => void;
  toggleTaskSignal: (id: string) => void;
  
  // Document management
  addDocument: (initiativeId: string, doc: Omit<InitiativeDocument, 'id' | 'initiativeId' | 'uploadedAt'>) => InitiativeDocument;
  updateDocument: (id: string, updates: Partial<InitiativeDocument>) => void;
  deleteDocument: (id: string) => void;
  
  // Selection
  selectInitiative: (id: string | null) => void;
  
  // Computed
  getInitiativeWithMetrics: (id: string) => InitiativeWithMetrics | null;
  getTasksByInitiative: (initiativeId: string) => InitiativeTask[];
  getDocumentsByInitiative: (initiativeId: string) => InitiativeDocument[];
  
  // Bulk operations
  importTasks: (initiativeId: string, tasks: Partial<InitiativeTask>[]) => InitiativeTask[];
}

// ============================================
// UTILITIES
// ============================================

const generateId = () => `init_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateTaskId = () => `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateDocId = () => `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============================================
// STORE
// ============================================

export const useInitiativeStore = create<InitiativeState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state with default AI Automation initiative
        initiatives: [DEFAULT_INITIATIVE],
        tasks: [],
        documents: [],
        selectedInitiativeId: null,
        
        // ========== INITIATIVE CRUD ==========
        
        createInitiative: (data) => {
          const initiative: Initiative = {
            id: generateId(),
            name: data.name,
            description: data.description,
            owner: data.owner,
            status: data.status,
            priority: data.priority,
            icon: data.icon,
            color: data.color,
            targetDate: data.targetDate,
            tags: data.tags,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isDefault: false,
          };
          
          set(state => ({
            initiatives: [...state.initiatives, initiative],
          }));
          
          return initiative;
        },
        
        updateInitiative: (id, updates) => {
          set(state => ({
            initiatives: state.initiatives.map(init =>
              init.id === id
                ? { ...init, ...updates, updatedAt: new Date().toISOString() }
                : init
            ),
          }));
        },
        
        deleteInitiative: (id) => {
          const initiative = get().initiatives.find(i => i.id === id);
          
          // Don't allow deleting default initiatives
          if (initiative?.isDefault) {
            console.warn('Cannot delete default initiative');
            return;
          }
          
          set(state => ({
            initiatives: state.initiatives.filter(i => i.id !== id),
            tasks: state.tasks.filter(t => t.initiativeId !== id),
            documents: state.documents.filter(d => d.initiativeId !== id),
            selectedInitiativeId: state.selectedInitiativeId === id ? null : state.selectedInitiativeId,
          }));
        },
        
        // ========== TASK CRUD ==========
        
        createTask: (initiativeId, data) => {
          console.log('[InitiativeStore] createTask called with initiativeId:', initiativeId);
          console.log('[InitiativeStore] task data:', data);

          const task: InitiativeTask = {
            id: generateTaskId(),
            initiativeId,
            title: data.title || 'New Task',
            description: data.description,
            owner: data.owner,
            status: data.status || 'Not Started',
            priority: data.priority || 'Medium',
            targetDate: data.targetDate,
            isSignal: data.isSignal || false,
            notes: data.notes,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          console.log('[InitiativeStore] Created task object:', task);

          set(state => {
            console.log('[InitiativeStore] Current tasks count:', state.tasks?.length || 0);
            const newTasks = [...(state.tasks || []), task];
            console.log('[InitiativeStore] New tasks count:', newTasks.length);
            return { tasks: newTasks };
          });

          // Update initiative updatedAt
          get().updateInitiative(initiativeId, {});

          console.log('[InitiativeStore] Task created successfully, returning:', task.id);

          // Refresh global signals to include this task
          refreshGlobalSignals();

          return task;
        },

        updateTask: (id, updates) => {
          set(state => ({
            tasks: state.tasks.map(task =>
              task.id === id
                ? { ...task, ...updates, updatedAt: new Date().toISOString() }
                : task
            ),
          }));

          // Update parent initiative
          const task = get().tasks.find(t => t.id === id);
          if (task) {
            get().updateInitiative(task.initiativeId, {});
          }

          // Refresh global signals
          refreshGlobalSignals();
        },

        deleteTask: (id) => {
          const task = get().tasks.find(t => t.id === id);

          set(state => ({
            tasks: state.tasks.filter(t => t.id !== id),
          }));

          // Update parent initiative
          if (task) {
            get().updateInitiative(task.initiativeId, {});
          }

          // Refresh global signals
          refreshGlobalSignals();
        },
        
        toggleTaskStatus: (id) => {
          const task = get().tasks.find(t => t.id === id);
          if (!task) return;
          
          const newStatus = task.status === 'Done' ? 'Not Started' : 'Done';
          get().updateTask(id, { status: newStatus });
        },
        
        toggleTaskSignal: (id) => {
          const task = get().tasks.find(t => t.id === id);
          if (!task) return;
          
          get().updateTask(id, { isSignal: !task.isSignal });
        },
        
        // ========== DOCUMENT MANAGEMENT ==========
        
        addDocument: (initiativeId, doc) => {
          const document: InitiativeDocument = {
            id: generateDocId(),
            initiativeId,
            filename: doc.filename,
            fileType: doc.fileType,
            status: doc.status || 'pending',
            extractedTaskCount: doc.extractedTaskCount,
            error: doc.error,
            rawContent: doc.rawContent,
            uploadedAt: new Date().toISOString(),
          };
          
          set(state => ({
            documents: [...state.documents, document],
          }));
          
          return document;
        },
        
        updateDocument: (id, updates) => {
          set(state => ({
            documents: state.documents.map(doc =>
              doc.id === id ? { ...doc, ...updates } : doc
            ),
          }));
        },
        
        deleteDocument: (id) => {
          set(state => ({
            documents: state.documents.filter(d => d.id !== id),
          }));
        },
        
        // ========== SELECTION ==========
        
        selectInitiative: (id) => {
          set({ selectedInitiativeId: id });
        },
        
        // ========== COMPUTED ==========
        
        getInitiativeWithMetrics: (id) => {
          const initiatives = get().initiatives || [];
          const initiative = initiatives.find(i => i.id === id);
          if (!initiative) return null;
          
          const tasks = (get().tasks || []).filter(t => t.initiativeId === id);
          const documents = (get().documents || []).filter(d => d.initiativeId === id);
          
          const totalTasks = tasks.length;
          const completedTasks = tasks.filter(t => t.status === 'Done').length;
          const inProgressTasks = tasks.filter(t => t.status === 'In Progress').length;
          const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
          
          return {
            ...initiative,
            totalTasks,
            completedTasks,
            inProgressTasks,
            progress,
            documentCount: documents.length,
          };
        },
        
        getTasksByInitiative: (initiativeId) => {
          return (get().tasks || []).filter(t => t.initiativeId === initiativeId);
        },
        
        getDocumentsByInitiative: (initiativeId) => {
          return (get().documents || []).filter(d => d.initiativeId === initiativeId);
        },
        
        // ========== BULK OPERATIONS ==========
        
        importTasks: (initiativeId, tasksData) => {
          const newTasks: InitiativeTask[] = tasksData.map(data => ({
            id: generateTaskId(),
            initiativeId,
            title: data.title || 'Imported Task',
            description: data.description,
            owner: data.owner,
            status: data.status || 'Not Started',
            priority: data.priority || 'Medium',
            targetDate: data.targetDate,
            isSignal: data.isSignal || false,
            notes: data.notes,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }));
          
          set(state => ({
            tasks: [...state.tasks, ...newTasks],
          }));
          
          // Update initiative
          get().updateInitiative(initiativeId, {});
          
          return newTasks;
        },
      }),
      {
        name: APP_CONFIG.STORAGE_KEYS.INITIATIVES,
        version: 1,
        merge: (persistedState: any, currentState: any) => ({
          ...currentState,
          ...persistedState,
          // Ensure arrays are always arrays
          initiatives: Array.isArray(persistedState?.initiatives) ? persistedState.initiatives : [],
          tasks: Array.isArray(persistedState?.tasks) ? persistedState.tasks : [],
          documents: Array.isArray(persistedState?.documents) ? persistedState.documents : [],
        }),
      }
    ),
    { name: 'ceo-initiative-store' }
  )
);

// ============================================
// SELECTORS
// ============================================

/**
 * Get all initiatives with metrics
 */
export const useInitiativesWithMetrics = () => {
  return useInitiativeStore(state => 
    (state.initiatives || []).map(init => state.getInitiativeWithMetrics(init.id)!).filter(Boolean)
  );
};

/**
 * Get active initiatives only
 */
export const useActiveInitiatives = () => {
  return useInitiativeStore(state =>
    (state.initiatives || []).filter(i => i.status === 'Active')
  );
};

/**
 * Get selected initiative with metrics
 */
export const useSelectedInitiative = () => {
  return useInitiativeStore(state => {
    if (!state.selectedInitiativeId) return null;
    return state.getInitiativeWithMetrics(state.selectedInitiativeId);
  });
};

/**
 * Get task counts across all initiatives
 */
export const useInitiativeTaskCounts = () => {
  return useInitiativeStore(state => {
    const tasks = state.tasks || [];
    return {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'Done').length,
      inProgress: tasks.filter(t => t.status === 'In Progress').length,
      notStarted: tasks.filter(t => t.status === 'Not Started').length,
      signals: tasks.filter(t => t.isSignal && t.status !== 'Done').length,
    };
  });
};
